package dev.lingvo.site.pages.app

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.flexGrow
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import dev.lingvo.site.api.ApiClient
import dev.lingvo.site.state.WorkspaceActionClaim
import dev.lingvo.site.state.WorkspacePrivilege
import dev.lingvo.site.state.runWorkspaceAction
import dev.lingvo.site.state.requestWorkspaceAction
import dev.lingvo.site.components.PlainLinkStyle
import dev.lingvo.site.ds.LAlert
import dev.lingvo.site.ds.LAlertVariant
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonSize
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LBracesIcon
import dev.lingvo.site.ds.LCard
import dev.lingvo.site.ds.LCodeBlock
import dev.lingvo.site.ds.LEmptyState
import dev.lingvo.site.ds.LBotIcon
import dev.lingvo.site.ds.LGitBranchIcon
import dev.lingvo.site.ds.LIconBadge
import dev.lingvo.site.ds.LMuted
import dev.lingvo.site.ds.LSectionTitle
import dev.lingvo.site.ds.LSparkleIcon
import dev.lingvo.site.ds.LToastVariant
import dev.lingvo.site.ds.LocalToasts
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.TypeScale
import dev.lingvo.site.ds.PageHeader
import dev.lingvo.site.ds.toSitePalette
import dev.lingvo.site.state.WorkspaceRequestContext
import dev.lingvo.site.state.WorkspaceRequestKind
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.dom.Text
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tr

/**
 * Three doors, not a wizard.
 *
 * Equally weighted, because which one is right depends entirely on who is reading — a developer with
 * a repo, a developer at a terminal, and somebody evaluating the product have three different first
 * moves, and a wizard picks for all of them.
 *
 * "Start blank" is present and demoted rather than absent: somebody who knows what they want should
 * not have to pick a door to get past this page.
 */
@Page("/app/start")
@Layout(".components.layouts.AppLayout")
@Composable
fun StartPage() {
    val ctx = rememberPageContext()
    val scope = rememberCoroutineScope()
    val toasts = LocalToasts.current
    val workspaceContext = WorkspaceRequestContext.current()
    var busy by remember(workspaceContext) { mutableStateOf(false) }
    var message by remember(workspaceContext) { mutableStateOf<String?>(null) }
    val canManageProjects = workspaceContext?.canManageProjects == true
    val sampleClaim = WorkspaceActionClaim.capture(workspaceContext, WorkspacePrivilege.SAMPLE_PROJECT)
    val sampleTargetsThisWorkspace =
        workspaceContext?.decision(WorkspaceRequestKind.SAMPLE_PROJECT)?.allowed == true
    val sampleAllowed = canManageProjects && sampleTargetsThisWorkspace

    Column(Modifier.fillMaxWidth().gap(Space.XL)) {
        PageHeader(
            t("app.start.get_started", "Get started"),
            t("app.start.three_ways_in_none_of_them", "Three ways in. None of them locks you out of the others."),
        )

        // **Belonging to no workspace is not a role refusal, and this page told it as one.**
        //
        // `AuthGuard` renders nothing until the session is authenticated, and `WorkspaceSelection`
        // falls back to the strongest membership, so a null context here means exactly one thing:
        // this account is in no workspace at all. `canManageProjects` was then false and the sample
        // door said "Only an owner or an admin can add a project — ask one of them", naming a
        // person who does not exist, while the other two doors led to a GitHub install nothing can
        // claim and a `lingvo push` with nowhere to land. `/app` has always answered this case with
        // the one action that fixes it.
        if (workspaceContext == null) {
            LEmptyState(
                title = t("app.start.you_are_not_in_a_workspace", "You are not in a workspace yet"),
                description = t("app.start.a_workspace_holds_your_projects_your", "A workspace holds your projects, your team and your billing. Every way " +
                    "in below needs one, so start by making a workspace or asking whoever invited " +
                    "you to send the invitation again."),
                action = {
                    LButton(
                        label = t("app.start.create_a_workspace", "Create a workspace"),
                        onClick = { ctx.router.tryRoutingTo("/app/workspaces") },
                        variant = LButtonVariant.PRIMARY,
                    )
                },
            )
            return@Column
        }

        message?.let {
            LAlert(
                t("app.start.that_didnt_work", "That didn't work"),
                it,
                variant = LAlertVariant.WARNING,
                live = true,
                // **A notice with no next move is where this stopped.** The one repeatable cause is
                // a sample this workspace already owns: the second request is refused by the
                // `projects_one_sample_per_org` index and comes back as a generic failure, so the
                // obvious next act — press it again — asks for it a third time and tells the reader
                // nothing. The projects list is where the sample they already have is.
                action = {
                    LButton(
                        t("app.start.see_your_projects", "See your projects"),
                        { ctx.router.tryRoutingTo("/app/projects") },
                        variant = LButtonVariant.SECONDARY,
                        size = LButtonSize.SM,
                    )
                },
            )
        }

        Door(
            title = t("app.start.set_up_with_the_cli", "Set up with the CLI"),
            body = t("app.start.pair_this_browser_with_your_terminal", "Pair this browser with your terminal — no token to copy, nothing in your " +
                "shell history. Then push your existing strings from the repository."),
            // **What it costs, before the click.** Three doors of equal weight and no way to tell
            // which one is the five-minute one is a choice made by guessing.
            effort = t("app.start.about_five_minutes_at_a_terminal", "About five minutes, at a terminal."),
            icon = { LBracesIcon() },
        ) {
            // Copying the first command in the funnel used to acknowledge nothing at all:
            // `LCodeBlock` takes an `onCopied` and eighteen call sites elsewhere raise a toast with
            // it, and this one passed none.
            CliSetupSteps(
                workspaceSlug = workspaceContext.workspace.slug,
                onCopied = {
                    toasts.show(
                        tr("app.start.copied_to_your_clipboard", "Copied to your clipboard"),
                        LToastVariant.SUCCESS,
                    )
                },
            )
        }

        Door(
            title = t("app.start.explore_a_sample_project", "Explore a sample project"),
            // The workspace claim is made only where it is true.
            body = if (sampleAllowed) {
                t("app.start.five_locales_real_plurals_and_a", "Five locales, real plurals and a few deliberate QA problems to find. It " +
                    "lands in this workspace so you can edit it, break it and delete it — and it " +
                    "does not use up your project slot.")
            } else {
                t("app.start.five_locales_real_plurals_and_a_2", "Five locales, real plurals and a few deliberate QA problems to find. It " +
                    "does not use up your project slot.")
            },
            effort = t("app.start.about_a_minute_nothing_to_install", "About a minute. Nothing to install."),
            icon = { LSparkleIcon() },
        ) {
            when {
                sampleAllowed -> LButton(
                    t("app.start.load_the_sample", "Load the sample"),
                    {
                        val claim = sampleClaim
                        if (claim != null && !busy) {
                            busy = true
                            message = null
                            scope.launch {
                                runWorkspaceAction(
                                    claim, WorkspaceRequestContext::current,
                                    setBusy = { busy = it },
                                    transport = {
                                        val project = ApiClient.createSampleProject(it)
                                        requestWorkspaceAction(claim, WorkspaceRequestContext::current) {
                                            sampleDestination(project.slug)
                                        }
                                    },
                                    onSuccess = { ctx.router.tryRoutingTo(it) },
                                    onFailure = { message = it.message ?: tr("app.start.check_your_connection_and_retry", "Check your connection and retry.") },
                                )
                            }
                        }
                    },
                    enabled = !busy,
                    loading = busy,
                )
                // Two different refusals, and they send the reader to two different people. Told as
                // one sentence, a translator was sent to ask about a workspace that is fine.
                !canManageProjects -> LAlert(
                    t("app.start.only_an_owner_or_admin_can", "Only an owner or an admin can add a project"),
                    t("app.start.ask_one_of_them_to_load", "Ask one of them to load the sample, or open a project that is already " +
                        "shared with you."),
                    variant = LAlertVariant.INFO,
                )
                else -> LAlert(
                    t("app.start.the_sample_cannot_pick_a_workspace", "Sample setup is unavailable here"),
                    t("app.start.you_are_in_more_than_one", "You can still explore an existing project or start with your own strings."),
                    variant = LAlertVariant.INFO,
                )
            }
        }

        Door(
            title = t("app.start.import_from_github", "Import from GitHub"),
            // The scope is stated **before** the click, because the person deciding whether to install
            // a GitHub App is often not the person who can approve one, and "we'll ask for
            // permissions later" is how that request gets refused.
            body = t("app.start.read_only_access_to_the_repositorys", "Read-only access to the repository's contents. No writes, no pull requests, " +
                "no access to your code beyond the localization files you pick."),
            effort = t("app.start.about_two_minutes_plus_whoever_approves", "About two minutes, plus whoever approves the install."),
            icon = { LGitBranchIcon() },
        ) {
            // Secondary, deliberately. Three doors, one accent: the sample is the fastest way in
            // and keeps it. Two accent-filled buttons on one screen answer "which one" with
            // "either", which is the question this page exists to answer.
            LButton(
                t("app.start.import_from_github", "Import from GitHub"),
                { ctx.router.tryRoutingTo("/app/start/github") },
                variant = LButtonVariant.SECONDARY,
            )
        }

        // **The fourth door.** Connecting an agent was the one way in the product supports and the
        // start page did not offer — and `/device`, the page somebody is told to visit, had no
        // inbound link from anywhere in the app. The copy names the outcome rather than the
        // mechanism: somebody choosing this wants Claude Code to translate in their repo, not to
        // "configure MCP".
        Door(
            title = t("app.start.let_an_agent_do_it", "Let an agent do it"),
            body = t("app.start.hand_claude_code_or_cursor_a", "Hand Claude Code or Cursor a credential scoped to this workspace. It reads the " +
                "style guide, the glossary and every string's own context — and everything it " +
                "writes waits for you to accept it."),
            effort = t("app.start.about_a_minute_and_it_proposes", "About a minute, and it proposes rather than writes."),
            icon = { LBotIcon() },
        ) {
            // Secondary, not a second primary: this page has exactly one filled button by decision.
            LButton(
                t("app.start.connect_an_agent", "Connect an agent"),
                { ctx.router.tryRoutingTo("/app/settings/agents/new") },
                variant = LButtonVariant.SECONDARY,
            )
        }

        // One honest line, and it is the differentiator.
        LMuted(
            t("app.start.lingvo_never_charges_by_word_or_2", "Lingvo never charges by word or by key. Bring your own provider key and machine " +
                "translation costs no credits at all."),
        )
        // Styled, because a bare `Link` inherits the user agent's `blue`/`aqua` — neither of which
        // is in either palette, and a second accent on a page whose whole job is ranking three doors.
        Link(
            path = "/app/projects/new",
            modifier = PlainLinkStyle.toModifier()
                .color(ColorMode.current.toSitePalette().accent),
        ) { Text(t("app.start.or_start_with_a_blank_project", "Or start with a blank project")) }
    }
}

/**
 * Where "Load the sample" actually lands.
 *
 * The project overview reports state — readiness, credits, files, activity — and the sample was
 * seeded with deliberate gaps and QA problems precisely so that there is something to *do*. The act
 * this whole screen exists to reach is "translated something", so the door ends in the editor, on
 * the target language furthest behind. In the seed that is Russian, at five strings of forty-five.
 *
 * **Every failure falls back to the overview rather than blocking.** The sample already exists by
 * the time this runs: a second request that did not answer is no reason to strand somebody on a
 * spinner or to report a failure for work that succeeded. A project whose targets are all complete
 * has no queue to open and lands there too.
 */
private suspend fun sampleDestination(slug: String): String {
    val overview = "/app/projects/$slug"
    val project = try {
        ApiClient.getProject(slug)
    } catch (cancelled: CancellationException) {
        throw cancelled
    } catch (_: Throwable) {
        return overview
    }
    val furthestBehind = project.progress
        .filter { it.locale != project.sourceLocale && it.percentTranslated < 100.0 }
        .minByOrNull { it.percentTranslated }
        ?: return overview
    return "/app/projects/$slug/translate/${furthestBehind.locale}"
}

/**
 * The three commands that take somebody from nothing to pushed strings.
 *
 * Separate from the page so it can be rendered without a router, and so the sequence has one
 * definition: the door used to start at the second step, and somebody without the binary typed
 * `lingvo login` first, got "command not found", and had nowhere on the page to go.
 */
@Composable
internal fun CliSetupSteps(workspaceSlug: String = "my-workspace", onCopied: () -> Unit = {}) {
    // Commands to *run*, so they are copyable rather than decorative: retyping
    // `brew install merkost/lingvo/lingvo` by hand is how a first step fails on a typo.
    Snippet(t("app.start.install", "Install"), "brew install merkost/lingvo/lingvo", onCopied)
    Snippet(t("app.start.pair_this_browser", "Pair this browser"), "lingvo login --device", onCopied)
    // **`/device` had no inbound link from anywhere in the product.** It is the page the command
    // above tells somebody to open, and until now the only way to reach it was to read the CLI's
    // own output carefully enough to type the URL.
    Link(path = "/device") { Text(t("app.start.enter_the_code_it_shows_you", "Enter the code it shows you")) }
    // Hosted creation is a separate step from local configuration. Keep it in the copyable
    // sequence, with this workspace's slug, so the first push has a project to resolve.
    LMuted(
        t("app.start.lingvo_init_only_writes_lingvo_yaml", "Run these commands in your repository. init writes lingvo.yaml; project create " +
            "sets up the hosted project in this workspace. Replace de and ja with your target " +
            "languages, then preview and upload your strings."),
    )
    Snippet(
        t("app.start.push_your_strings", "Push your strings"),
        "lingvo init\nlingvo project create --workspace $workspaceSlug --locale de --locale ja\n" +
            "lingvo push --dry-run\nlingvo push\n",
        onCopied,
    )
    Link(
        path = "/docs#install",
        modifier = PlainLinkStyle.toModifier()
            .color(ColorMode.current.toSitePalette().accent),
    ) { Text(t("app.start.not_on_macos", "Not on macOS?")) }
}

@Composable
private fun Door(
    title: String,
    body: String,
    /** One line saying what this route costs, so the three can be compared without trying them. */
    effort: String,
    /** The door's mark, in the badge every section header in the product stands its glyph in. */
    icon: @Composable () -> Unit,
    action: @Composable () -> Unit,
) {
    LCard(Modifier.fillMaxWidth()) {
        Column(Modifier.fillMaxWidth().gap(Space.MD)) {
            Row(Modifier.fillMaxWidth().gap(Space.MD), verticalAlignment = Alignment.CenterVertically) {
                LIconBadge { icon() }
                Column(Modifier.flexGrow(1).gap(Space.XXS)) {
                    // The card's identity rather than a section label, so it keeps `HeadingSmall` —
                    // and it is a heading, so a reader can move between the three doors.
                    LSectionTitle(title, Modifier.fontSize(TypeScale.HeadingSmall))
                    LMuted(effort, Modifier.fontSize(TypeScale.Small))
                }
            }
            LMuted(body)
            action()
        }
    }
}

@Composable
private fun Snippet(label: String, command: String, onCopied: () -> Unit = {}) {
    LCodeBlock(text = command, label = label, modifier = Modifier.fillMaxWidth(), onCopied = onCopied)
}
